/**
 * Self-test suite for the WEB_DEVICE_ID single-source-of-truth invariant.
 *
 * Verifies that:
 *   1. WEB_DEVICE_ID is exported from `e2ee/crypto/session` and equals 1
 *      (mirrors iOS, see docs/web-device-id.md).
 *   2. WEB_DEVICE_ID is a positive integer.
 *   3. The pure-function linter `validateNoStrayDeviceIdLiterals` correctly
 *      handles clean text, dirty text, and allowlisted files. Same regex
 *      semantics as `scripts/lint-device-id.mjs` so the CI lint and the
 *      browser autorun share a single behaviour spec.
 *
 * Pure-logic only. No fs, no network, no IndexedDB — safe to invoke from any
 * context including headless vitest.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 */

import { WEB_DEVICE_ID } from "../crypto/session";
import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

// ---------------------------------------------------------------------------
// Pure-function linter — shared with scripts/lint-device-id.mjs
// ---------------------------------------------------------------------------

/**
 * Files that are *expected* to contain numeric `deviceId` literals. Each
 * entry is a path relative to `packages/client/components/e2ee/` in
 * forward-slash form. Membership is checked with exact-equality OR suffix
 * match so callers can pass either form.
 *
 * Keep in sync with `scripts/lint-device-id.mjs` ALLOWLIST.
 */
export const DEFAULT_ALLOWLIST: readonly string[] = [
  "sender-keys/SenderKeySelfTest.ts",
  "sender-signatures/SenderSignaturesSelfTest.ts",
  "svr/SVRSelfTest.ts",
  "linked-device/LinkedDeviceProvisioningSelfTest.ts",
  // This file embeds deviceId literals as STRING fixtures handed to the
  // pure linter function below. It is the lint checking itself.
  "build-hygiene/DeviceIdSelfTest.ts",
];

export interface DeviceIdFinding {
  readonly filename: string;
  readonly line: number;
  readonly column: number;
  readonly value: string;
  readonly snippet: string;
}

export interface DeviceIdValidationResult {
  readonly findings: readonly DeviceIdFinding[];
  readonly allowed: boolean;
}

/**
 * Pure logic: scan `text` for `deviceId: <int>` / `deviceId = <int>`
 * literals and return per-occurrence findings.
 *
 * Identical semantics to `scripts/lint-device-id.mjs`. The browser autorun
 * exercises this same function against synthetic input so a regression in
 * the regex shows up in both contexts simultaneously.
 *
 * @param text       file contents to scan
 * @param filename   short identifier used in finding output (typically the
 *                   relative path under `e2ee/`)
 * @param allowlist  set of filenames for which findings are expected. A
 *                   match is exact equality OR suffix-match.
 */
export function validateNoStrayDeviceIdLiterals(
  text: string,
  filename: string,
  allowlist: readonly string[],
): DeviceIdValidationResult {
  const findings: DeviceIdFinding[] = [];
  const allowed = allowlist.some(
    (a) => filename === a || filename.endsWith("/" + a) || filename.endsWith(a),
  );
  const lines = text.split(/\r?\n/);
  // Match deviceId: NN or deviceId = NN where NN is a non-empty integer.
  // Negative lookahead on \d so we do not match identifiers like `deviceId1`.
  const re = /deviceId\s*[:=]\s*(\d+)(?!\d)/g;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    let m: RegExpExecArray | null;
    re.lastIndex = 0;
    while ((m = re.exec(line)) !== null) {
      findings.push({
        filename,
        line: i + 1,
        column: m.index + 1,
        value: m[1]!,
        snippet: line.trim(),
      });
    }
  }
  return { findings, allowed };
}

// ---------------------------------------------------------------------------
// Individual test cases
// ---------------------------------------------------------------------------

/** 1. WEB_DEVICE_ID is exported and equals 1. */
function testConstantExported(): TestCase {
  const name = "WEB_DEVICE_ID is exported from session module";
  if (typeof WEB_DEVICE_ID !== "number") {
    return fail(name, `expected number, got ${typeof WEB_DEVICE_ID}`);
  }
  if (WEB_DEVICE_ID !== 1) {
    return fail(name, `expected 1 (iOS mirror), got ${WEB_DEVICE_ID}`);
  }
  return pass(name);
}

/** 2. WEB_DEVICE_ID is a positive integer. */
function testConstantIsPositiveInteger(): TestCase {
  const name = "WEB_DEVICE_ID is a positive integer";
  if (!Number.isInteger(WEB_DEVICE_ID)) {
    return fail(name, `not an integer: ${String(WEB_DEVICE_ID)}`);
  }
  if (WEB_DEVICE_ID <= 0) {
    return fail(name, `not positive: ${String(WEB_DEVICE_ID)}`);
  }
  return pass(name);
}

/** 3. Clean sample returns zero findings. */
function testNoFindingsOnCleanSample(): TestCase {
  const name =
    "pure-function validateNoStrayDeviceIdLiterals returns no findings for a sample without literals";
  const cleanSample = [
    "import { WEB_DEVICE_ID } from './session';",
    "const ADDR = `${userId}.${WEB_DEVICE_ID}`;",
    "function encryptFor(deviceId: number) {",
    "  return cipher.encrypt(deviceId);",
    "}",
  ].join("\n");
  const result = validateNoStrayDeviceIdLiterals(
    cleanSample,
    "fake/clean.ts",
    DEFAULT_ALLOWLIST,
  );
  if (result.findings.length !== 0) {
    return fail(
      name,
      `expected 0 findings, got ${result.findings.length}: ${JSON.stringify(result.findings)}`,
    );
  }
  if (result.allowed) {
    return fail(name, "fake/clean.ts must not be allowlisted");
  }
  return pass(name);
}

/** 4. Dirty sample with `deviceId: 2` produces a finding. */
function testFlagsStrayLiteral(): TestCase {
  const name =
    "pure-function validateNoStrayDeviceIdLiterals flags deviceId: 2 in a sample";
  const dirtySample = [
    "const peer = {",
    "  userId: 'bob',",
    "  deviceId: 2,",
    "};",
  ].join("\n");
  const result = validateNoStrayDeviceIdLiterals(
    dirtySample,
    "production/unlisted.ts",
    DEFAULT_ALLOWLIST,
  );
  if (result.findings.length !== 1) {
    return fail(
      name,
      `expected 1 finding, got ${result.findings.length}`,
    );
  }
  const f = result.findings[0]!;
  if (f.value !== "2") {
    return fail(name, `expected value '2', got '${f.value}'`);
  }
  if (f.line !== 3) {
    return fail(name, `expected line 3, got ${f.line}`);
  }
  if (result.allowed) {
    return fail(name, "production/unlisted.ts must not be allowlisted");
  }
  return pass(name);
}

/** 5. Allowlist suppresses surfacing of findings as violations. */
function testAllowlistRespected(): TestCase {
  const name =
    "pure-function validateNoStrayDeviceIdLiterals respects allowlist";
  const fixtureSample = [
    "const ids = [",
    "  { groupId: 'g1', senderId: 'alice', deviceId: 1 },",
    "  { groupId: 'g1', senderId: 'bob',   deviceId: 2 },",
    "];",
  ].join("\n");

  // Same content, two filenames: one allowlisted, one not.
  const onAllowed = validateNoStrayDeviceIdLiterals(
    fixtureSample,
    "sender-keys/SenderKeySelfTest.ts",
    DEFAULT_ALLOWLIST,
  );
  const onForbidden = validateNoStrayDeviceIdLiterals(
    fixtureSample,
    "production/unlisted.ts",
    DEFAULT_ALLOWLIST,
  );

  if (onAllowed.findings.length !== 2) {
    return fail(
      name,
      `allowlisted file: expected 2 findings (still surfaced), got ${onAllowed.findings.length}`,
    );
  }
  if (!onAllowed.allowed) {
    return fail(name, "sender-keys/SenderKeySelfTest.ts must be allowlisted");
  }
  if (onForbidden.findings.length !== 2) {
    return fail(
      name,
      `forbidden file: expected 2 findings, got ${onForbidden.findings.length}`,
    );
  }
  if (onForbidden.allowed) {
    return fail(name, "production/unlisted.ts must not be allowlisted");
  }

  // Suffix-match: passing just the basename should still match.
  const bySuffix = validateNoStrayDeviceIdLiterals(
    fixtureSample,
    "packages/client/components/e2ee/sender-keys/SenderKeySelfTest.ts",
    DEFAULT_ALLOWLIST,
  );
  if (!bySuffix.allowed) {
    return fail(name, "allowlist suffix-match failed");
  }

  return pass(name);
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "WEB_DEVICE_ID single source of truth",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      testConstantExported(),
      testConstantIsPositiveInteger(),
      testNoFindingsOnCleanSample(),
      testFlagsStrayLiteral(),
      testAllowlistRespected(),
    ]),
});
